import React, { createContext, useContext, useEffect, useState } from 'react';
// material
import { AppBar, Container, Dialog, Grid, IconButton, Toolbar, Typography, Stack, Box, CircularProgress, Checkbox, FormControlLabel } from '@mui/material';
// components
import Page from '../components/Page';
import axios from "axios";
import AnnonceSelector from 'src/components/FormAnnonceList/AnnonceSelector';
import CloseIcon from '@mui/icons-material/Close';
import AnnonceDetails from 'src/sections/@dashboard/products/AnnonceDetails';
import { useParams, Navigate, useSearchParams } from 'react-router-dom';
import ShopProductCard from 'src/sections/@dashboard/products/ProductCard';
import InfiniteScroll from 'react-infinite-scroll-component';
import CustomSpinner from './CustomSpinner';
import { getCategories } from 'src/service/categoriesApi';
import { getCustomersAds } from 'src/service/customerApi';
import AuthContext from 'src/contexts/AuthContext';
import { useNotification } from 'src/contexts/NotificationContext';
import { Badge } from '@mui/icons-material';
import { keyframes } from '@mui/system';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';

// Animation de la loupe
const rotate = keyframes`
  from {
    transform: rotate(0deg) translateX(10px) rotate(0deg);
  }
  to {
    transform: rotate(360deg) translateX(10px) rotate(-360deg);
  }
`;

const LoadingContainer = styled(Box)({
  position: 'relative',
  width: '200px',
  height: '200px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const StaticLogo = styled('img')({
  width: '150px',
  height: '150px',
  filter: 'drop-shadow(0 0 8px rgba(0, 90, 160, 0.3))',
});

const RotatingLoupe = styled(SearchIcon)({
  position: 'absolute',
  color: '#0058a7',
  fontSize: '40px',
  animation: `${rotate} 1s linear infinite`,
  filter: 'drop-shadow(0 0 4px rgba(0, 90, 160, 0.2))',
  right: 'calc(50% - 85px)',
  top: '50%',
  transform: 'translateY(-50%)',
});

const SmallLoadingContainer = styled(Box)({
  position: 'relative',
  width: '100px',
  height: '100px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const SmallStaticLogo = styled('img')({
  width: '75px',
  height: '75px',
  filter: 'drop-shadow(0 0 8px rgba(0, 90, 160, 0.3))',
});

const SmallRotatingLoupe = styled(SearchIcon)({
  position: 'absolute',
  color: '#0058a7',
  fontSize: '20px',
  animation: `${rotate} 1s linear infinite`,
  filter: 'drop-shadow(0 0 4px rgba(0, 90, 160, 0.2))',
  right: 'calc(50% - 42px)',
  top: '50%',
  transform: 'translateY(-50%)',
});

// ----------------------------------------------------------------------
const initialState = {
    selectedCategory: null,
    setSelectedCategory: () => undefined,
    filteredAttributes: null,
    setFilteredAttributes: () => undefined,
    date: null,
    setDate: () => undefined,
    numObject: '',
    setNumObject: () => undefined,
    email: null,
    setEmail: () => undefined,
    name: null,
    setName: () => undefined,
    globalValue: null,
    setGlobalValue: () => undefined,
};
export const FoundContext = createContext(initialState);

export default function EcommerceShop({ status }) {
  const [selectedAnnonce, setSelectedAnnonce] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [foundAds, setFoundAds] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState();
  const [filteredAttributes, setFilteredAttributes] = useState([]);
  const [date, setDate] = useState(false);
  const [numObject, setNumObject] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [globalValue, setGlobalValue] = useState('');
  const [onlyWithConversations, setOnlyWithConversations] = useState(false);
  const { annonceID } = useParams() || null;
  const [hasMore, setHasMore] = useState(true);
  const [resultFilter, setResultFilter] = useState([]);
  const [count, setCount] = useState({
    prev: 0,
    next: 12,
  });
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const { customerId, isAuthenticated } = useContext(AuthContext);
  const { refreshTrigger } = useNotification();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (customerId) {
      fetchFoundAds(customerId);
    }
  }, [status, customerId, refreshTrigger, numObject]);



  useEffect(() => {
    let isMounted = true;
    
    getCategories().then((resp) => {
      if (isMounted) {
        setCategories(resp);
      }
    });
    
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    // Ne pas appeler makeFilter si on recherche par numéro d'objet
    // car fetchFoundAds s'en charge déjà
    if (!numObject || numObject.trim() === '') {
      makeFilter();
    }
  }, [selectedCategory, filteredAttributes, date, name, email, globalValue, onlyWithConversations]);

  // Vérifier les paramètres d'URL pour ouvrir automatiquement la modal
  useEffect(() => {
    const conversationId = searchParams.get('conversation');
    const tab = searchParams.get('tab');
    
    if (annonceID && conversationId && tab === 'chat') {
      // Trouver l'annonce correspondante
      const annonce = foundAds.find(ad => ad.id === parseInt(annonceID));
      if (annonce) {
        setSelectedAnnonce(annonce);
        setOpenDialog(true);
      }
    }
  }, [annonceID, searchParams, foundAds]);

  // Rediriger vers la page de connexion si non authentifié
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  const haveMore = () => {
    if (current.length >= resultFilter.length) {
      setHasMore(false);
      return;
    }

    const nextBatch = resultFilter.slice(count.prev + 12, count.next + 12);
    setCurrent(prev => [...prev, ...nextBatch]);
    setCount(prev => ({
      prev: prev.prev + 12,
      next: prev.next + 12,
    }));
  };

  const handleClickOpenDialog = (data) => {
    setSelectedAnnonce(data);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {

    setOpenDialog(false);
  };

  const urlStatus = status !== undefined ? `?status=${status}` : '';

  const fetchFoundAds = async (id) => {
    if (!id) return;
    
    setIsLoading(true);
    try {
      
      // Si on recherche par numéro d'objet, appeler l'API sans paramètre de statut
      // pour récupérer TOUS les objets de l'utilisateur
      const apiUrl = numObject && numObject.trim() !== '' ? '' : urlStatus;
      const data = await getCustomersAds(id, apiUrl);
      
      // Si on recherche par numéro d'objet, ne pas filtrer par statut
      if (numObject && numObject.trim() !== '') {
        setFoundAds(data); // Utiliser toutes les données sans filtrage par statut
        setTotal(data.length);
        makeFilter(data);
      } else {
        // Filtrer les objets perdus pour n'afficher que ceux où professionalId = customerId
        const filteredData = status === 'PERDU' 
          ? data.filter(ad => ad.professionalId === id)
          : data;

        setFoundAds(filteredData);
        setTotal(filteredData.length);
        
        // Toujours appliquer les filtres après le rafraîchissement
        // pour préserver le contexte de l'utilisateur
        if (annonceID) {
          makeFilter(filteredData);
        } else {
          // Appliquer les filtres actuels sur les nouvelles données
          makeFilter(filteredData);
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des annonces:', error);
      setFoundAds([]);
      setTotal(0);
      setResultFilter([]);
      setCurrent([]);
    } finally {
      setIsLoading(false);
    }
  };

    const makeFilter = (data = foundAds) => {

    let filteredData = [...data];

    // Recherche par numéro d'objet - PRIORITAIRE et EXCLUSIVE
    if (numObject && numObject.trim() !== '') {
      const numObjectInt = parseInt(numObject);
      if (!isNaN(numObjectInt)) {
        // Recherche exclusive par ID - ignore tous les autres filtres
        filteredData = filteredData.filter((a) => a.id === numObjectInt);
        
        // Appliquer directement le résultat sans autres filtres
        setResultFilter(filteredData);
        setCurrent(filteredData.slice(count.prev, count.next));
        return;
      }
    }

    // Si pas de recherche par numéro d'objet, appliquer tous les autres filtres
    // Email filter
        if (email) {
      filteredData = filteredData.filter((a) => 
        a.conversations && a.conversations.some((c) => 
          c.author && c.author.email && c.author.email.toLowerCase() === email.toLowerCase()
        )
      );
        }

    // Name filter
        if (name) {
      filteredData = filteredData.filter((a) => 
        a.annonceAttributes && a.annonceAttributes.some((c) => 
          c.isSecret && c.attributeValue && c.attributeValue.toLowerCase() === name.toLowerCase()
        )
      );
    }

    // Category filter (seulement si pas de recherche par numéro d'objet)
    if (annonceID) {
      filteredData = filteredData.filter((a) => a.id === parseInt(annonceID));
    } else if (selectedCategory) {
      filteredData = filteredData.filter((a) => a.category && a.category.id === selectedCategory);
    }

    // Date filter
    if (date) {
      filteredData = filteredData.filter((a) => {
        const adDate = new Date(a.date);
        const filterDate = new Date(date);
        return adDate.setHours(0, 0, 0, 0) === filterDate.setHours(0, 0, 0, 0);
      });
    }

    // Attributes filter
    if (filteredAttributes.length > 0) {
      filteredData = filteredData.filter((a) =>
        filteredAttributes.every((attribute) =>
          a.annonceAttributes && a.annonceAttributes.some((attr) =>
                    attribute.attributeValue !== -1
              ? attr.attribute === attribute.attribute &&
                attr.attributeValue && attr.attributeValue.toLowerCase() === attribute.attributeValue.toLowerCase()
              : attr.attribute != null
          )
        )
      );
    }

    // Global search filter
    if (globalValue) {
            const searchTerms = globalValue.toLowerCase().split(' ');
      filteredData = filteredData.filter((a) =>
        searchTerms.every((term) =>
          (a.category && a.category.name && a.category.name.toLowerCase().includes(term)) ||
          (a.annonceAttributes && a.annonceAttributes.some((attr) => 
            attr.attributeValue && attr.attributeValue.toLowerCase().includes(term)
          ))
        )
      );
    }

    // Filtre "avec conversations"
    if (onlyWithConversations) {
      filteredData = filteredData.filter((a) => Array.isArray(a.conversations) && a.conversations.length > 0);
    }

    // Tri par défaut : ID décroissant (plus récent en premier)
    filteredData.sort((a, b) => b.id - a.id);
    
    setResultFilter(filteredData);
    setCurrent(filteredData.slice(count.prev, count.next));
    };

  const updateUserAds = (data) => {
    // Si le statut a changé, rafraîchir complètement la liste
    const existingAd = foundAds.find((ad) => ad.id === data.id);
    if (existingAd && existingAd.status !== data.status) {
      console.log('Statut changé, rafraîchissement de la liste complète');
      fetchFoundAds(customerId);
      return;
    }
    
    // Sinon, mettre à jour seulement l'annonce spécifique
    const index = foundAds.findIndex((ad) => ad.id === data.id);
    const index2 = current.findIndex((ad) => ad.id === data.id);
    if (index !== -1) {
        const newAds = [...foundAds];
        newAds[index] = data;
        setFoundAds(newAds);
    }
    if (index2 !== -1) {
        const newAds = [...current];
        newAds[index2] = data;
        setCurrent(newAds);
    }
  }

  return (
    <Page title="Dashboard: Annonces">
      <FoundContext.Provider
        value={{
          selectedCategory,
          setSelectedCategory,
          filteredAttributes,
          setFilteredAttributes,
          date,
          setDate,
          numObject,
          setNumObject,
          name,
          setName,
          email,
          setEmail,
          globalValue,
          setGlobalValue,
          onlyWithConversations,
          setOnlyWithConversations,
        }}
      >
        <Container>
          <Stack spacing={3}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h4">
                Objets
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {resultFilter.length} Résultats
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={2}>
              <AnnonceSelector />
            </Stack>

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <LoadingContainer>
                  <StaticLogo src="/static/logo.svg" alt="Loading..." />
                  <RotatingLoupe />
                </LoadingContainer>
              </Box>
            ) : current.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Aucun objet trouvé
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 400 }}>
                  {resultFilter.length === 0 
                    ? "Il n'y a actuellement aucun objet correspondant à vos critères de recherche." 
                    : "Essayez de modifier vos filtres pour voir plus de résultats."
                  }
                </Typography>
              </Box>
            ) : (
              <InfiniteScroll
                dataLength={current.length}
                next={haveMore}
                hasMore={hasMore}
                loader={
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <SmallLoadingContainer>
                      <SmallStaticLogo src="/static/logo.svg" alt="Loading..." />
                      <SmallRotatingLoupe />
                    </SmallLoadingContainer>
                  </Box>
                }
                endMessage={
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Vous avez atteint la fin de la liste
                    </Typography>
                  </Box>
                }
                scrollThreshold="90%"
              >
                <Grid container spacing={3}>
                  {current.map((ads) => (
                    <Grid key={ads.id} item xs={12} sm={6} md={4}>
                      <ShopProductCard
                        ads={ads}
                        handleClickOpenDialog={handleClickOpenDialog}
                        fetchFoundAds={fetchFoundAds}
                      />
                    </Grid>
                  ))}
                </Grid>
              </InfiniteScroll>
            )}
          </Stack>
        </Container>

        {/* Annonce dialogue */}
        <Dialog 
          fullScreen 
          open={openDialog} 
          onClose={handleCloseDialog}
          TransitionProps={{
            onExited: () => setSelectedAnnonce(null)
          }}
          sx={{
            '& .MuiDialog-paper': {
              background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
            }
          }}
        >
          <AppBar 
            sx={{ 
              position: 'relative', 
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }}
          >
            <Toolbar>
              <IconButton
                edge="start"
                onClick={handleCloseDialog}
                aria-label="close"
                sx={{
                  color: '#1B3038',
                  '&:hover': {
                    backgroundColor: 'rgba(27, 48, 56, 0.1)',
                    transform: 'scale(1.1)'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <CloseIcon />
              </IconButton>
              <Typography 
                variant="h6" 
                sx={{ 
                  ml: 2, 
                  color: '#1B3038',
                  fontWeight: 600
                }}
              >
                Détails de l'objet
              </Typography>
            </Toolbar>
          </AppBar>

          <AnnonceDetails 
            selectedAnnonce={selectedAnnonce} 
            setSelectedAnnonce={setSelectedAnnonce} 
            categories={categories} 
            updateUserAds={updateUserAds}
            onClose={handleCloseDialog}
          />
        </Dialog>
      </FoundContext.Provider>
    </Page>
  );
}
